import {
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import mapboxgl from 'mapbox-gl';

import { LayerContext } from 'state/LayerContext';
import { useGetLayerQuery } from 'state/apiSlice';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  selectCurrentProjectInfo,
  selectHideSidebar,
  selectMapCenter,
  selectMapStyle,
  selectMapZoom,
  selectMaximize,
  selectRoadFilters,
  selectRouteSelection,
  selectSegmentId,
  selectTimeOfDay,
  setMapCenter,
  setMapZoom,
  setMaximize,
  setRouteSelection,
  setSelectedSegmentId,
  undoRouteSelection,
} from 'state/workflowSlice';

import { MAXIMIZE_MAP } from 'appConstants';
import { formatTimeOfDay, nowUnix } from 'features/common/datetimeUtils';
import { getSegmentLayerDefinition } from 'features/map/utils/mapLayerDefinitions';
import { ensureRtlLoaded } from 'features/map/utils/mapUtils';

import { useCallbackWithErrorHandling } from '../../app/ErrorHandling';
import { mapboxApiKey } from '../../appConstants';
import { LayerRouting } from '../../appTypes';
import {
  selectDisplayedBookmark,
  selectInLocationEditMode,
  setDisplayedBookmark,
} from '../../state/bookmarkSlices';
import {
  routeSelectionIsSavedRoute,
  useRouteInfo,
} from '../common/hooks/useRouteInfo';
import { isCtrlEvent } from '../common/utils';
import { useSegmentSelectionCrosswalk } from '../workflow_common/SegmentSelectionCrosswalk';
import { MapContext } from './MapContext';
import { MapLoadingSpinner } from './MapLoadingSpinner';
import { useMapResize } from './hooks/useMapResize';
import { useProjectBoundary } from './hooks/useProjectBoundary';
import { useRoadClassFilterTooltip } from './hooks/useRoadClassFilterTooltip';
import { useRoadFilters } from './hooks/useRoadFilters';
import { MapSettings } from './overlays/MapSettings';
import { OutsideBboxPrompt } from './overlays/OutsideBboxPrompt';
import ScreenshotExtras from './overlays/ScreenshotExtras';
import { RoadClassTooltip } from './tooltips/RoadClassTooltip/RoadClassTooltip';
import { latLngToJsonPoint, launderLatLng } from './utils/geography_utils';
import {
  getInitialCenter,
  getSelectionFromClickEvent,
  handleWindowKeyDownRouteSelectionUndo,
  initMap,
  mapHasLayer,
  mapPopSelectedSegment,
  redrawBaseSegments,
  redrawSelectedSegment,
  safeRemoveBookmarks,
  showBookmarks,
  updateMapLocation,
} from './utils/mapCommon';
import { MapControl } from './utils/mapControls';
import {
  kChartShowHideId,
  kLongTouchTimeMs,
  kSegmentLayerId,
  kSegmentSourceId,
} from './utils/map_constants';

// mapbogxl style should be imported BEFORE Map.css as it must override a handful of styles
import 'mapbox-gl/dist/mapbox-gl.css';

import 'features/map/Map.css';

import styles from './Map.module.css';

mapboxgl.accessToken = mapboxApiKey;

export type MapArgs = {
  enableRouteSelection?: boolean;
  extras?: ReactNode;
  drawSegmentLayer?: boolean;
  showRoutes?: boolean;
  routeLayerId?: string;
  chartShowhide?: boolean;
  hideControls?: boolean;
};

export function Map({
  enableRouteSelection = false,
  extras,
  drawSegmentLayer = true,
  showRoutes = true,
  routeLayerId = undefined,
  chartShowhide = true,
  hideControls = false,
}: MapArgs) {
  // https://docs.mapbox.com/help/tutorials/use-mapbox-gl-js-with-react/

  const dispatch = useAppDispatch();

  // useRef DOM refs and for variables accessed from map callbacks
  const { refMap, mapReady, setMapReady, refStyleReady } =
    useContext(MapContext);
  const refMapContainer = useRef(null); // DOM node reference
  const refRouting = useRef<LayerRouting | undefined>(undefined);
  const refTouchState = useRef(undefined);

  const [settingsOpen, setSettingsOpen] = useState<boolean>(false);
  const {
    roadFilterOpen,
    setRoadFilterOpen,
    anchorEl,
    handleRoadClassFilterClickCallback,
  } = useRoadClassFilterTooltip();
  const { layer } = useContext(LayerContext);
  const selectedSegmentId = useAppSelector(selectSegmentId);
  const selectedRoute = useAppSelector(selectRouteSelection);
  const maximize = useAppSelector(selectMaximize);
  const refMaximize = useRef(maximize);
  const currentMapCenter = useAppSelector(selectMapCenter);
  const mapZoom = useAppSelector(selectMapZoom);
  const displayedBookmark = useAppSelector(selectDisplayedBookmark);
  const inLocationEditMode = useAppSelector(selectInLocationEditMode);
  const refInLocationEditMode = useRef(inLocationEditMode);
  const mapStyle = useAppSelector(selectMapStyle);
  const hideSidebar = useAppSelector(selectHideSidebar);
  const timeOfDay = useAppSelector(selectTimeOfDay);
  const roadFilters = useAppSelector(selectRoadFilters);

  useMapResize(refMap.current, refMapContainer);
  useRoadFilters();
  const {
    feature: routeFeature,
    savedRoute,
    basicRouteInfo,
  } = useRouteInfo(!enableRouteSelection);

  const refSelection = useRef({
    segment: selectedSegmentId,
    routeSelection: selectedRoute,
    routeFeature,
    savedRoute,
    basicRouteInfo,
  });

  const userProject = useAppSelector(selectCurrentProjectInfo);

  // load segmentData for layer from REST api (or cache)
  const { currentData: layerData } = useGetLayerQuery(layer, { skip: !layer });
  useSegmentSelectionCrosswalk();
  // memoize copy of segments
  refRouting.current = useMemo(() => layerData?.routing, [layerData]);

  const carLayerVisible = true;

  useProjectBoundary(userProject.project_boundary);
  ensureRtlLoaded();

  const handleSegmentClickCallback = useCallbackWithErrorHandling(
    (event, isRouteAddClick = false) => {
      if (!mapHasLayer(refMap.current, kSegmentLayerId)) return;
      // console.log(`handleSegmentClickCallback`, event);
      if (refInLocationEditMode.current) {
        dispatch(setDisplayedBookmark(launderLatLng(event.lngLat)));
      } else {
        const routeSelectionSegments = routeSelectionIsSavedRoute(
          refSelection.current.routeSelection,
        )
          ? refSelection.current.savedRoute?.routeSegmentIds
          : refSelection.current.routeSelection;
        const {
          segmentId: newSelectedSegmentId,
          route,
          routingFailed,
        } = getSelectionFromClickEvent(
          event,
          refMap.current,
          refRouting,
          enableRouteSelection,
          isRouteAddClick,
          refSelection.current.segment,
          routeSelectionSegments,
          undefined,
          routeLayerId,
          () => dispatch(undoRouteSelection()),
        );
        if (routingFailed) return; // do nothing
        if (route) {
          // console.log('Setting Route selection: ', route);
          dispatch(setRouteSelection(route));
        } else if (route === null) {
          if (newSelectedSegmentId && enableRouteSelection) {
            dispatch(setRouteSelection([newSelectedSegmentId]));
          } else {
            dispatch(setRouteSelection(undefined));
          }
        }
        // console.log('Selection = ', newSelectedSegmentId);
        dispatch(setSelectedSegmentId(newSelectedSegmentId));
      }
    },
  );

  const isTouchDrag = () => {
    const last = refTouchState.current?.lastCoord;
    const first = refTouchState.current?.startCoord;
    const isDrag =
      last && // Last might not be set by touchmove if the finger has been still (which would mean not a drag)
      (Math.abs(first.x - last.x) > 10 || // Was the touch started nearby == not a drag
        Math.abs(first.y - last.y) > 10);
    return isDrag;
  };

  const handleTouchTimeout = (event) => {
    // console.log('Timeout event', event);
    refTouchState.current.timeout = undefined; // Clear timeout so touchend knows we're handling the touch
    refTouchState.current.isDrag = isTouchDrag();
    refTouchState.current.isLongTouch = true;
    if (!refTouchState.current.isDrag) {
      handleSegmentClickCallback(event, true);
    }
  };

  const handleTouchStart = useCallbackWithErrorHandling(
    (event: mapboxgl.MapTouchEvent) => {
      // Only handling single touch
      // console.log('Touch start', event);
      if (event.originalEvent.touches.length === 1) {
        refTouchState.current = {
          startTime: nowUnix(),
          startCoord: event.point,
          type: event.type,
          // Here' we're queuing a timeout event to handle long touches before they're finished
          // This means the user gets visual feedback of route building after kLongTouchTimeMs rather than
          // After they lift their finger (not knowing if they've held for long enough)
          timeout: setTimeout(handleTouchTimeout, kLongTouchTimeMs, event),
        };
      }
    },
  );

  const handleTouchMove = useCallbackWithErrorHandling(
    (event: mapboxgl.MapTouchEvent) => {
      // Keep track of if the user is moving their touch, and if so, how far
      if (event.originalEvent.touches.length === 1) {
        refTouchState.current.lastCoord = event.point;
      } else if (refTouchState.current.timeout) {
        // If we suddenly move to two+ touches, cancel the timeout
        clearTimeout(refTouchState.current.timeout);
      }
    },
  );

  const handleClick = useCallbackWithErrorHandling(
    (event: mapboxgl.MapMouseEvent) => {
      // console.log(`handleClick`, event, refTouchState.current);
      const isDrag = isTouchDrag();
      if (refTouchState.current) {
        if (refTouchState.current.timeout) {
          // We know that if timeout is defined, we have yet to trigger the timeout handler and therefore it is not a long press
          // console.log('Short touch, cancelling timeout');
          clearTimeout(refTouchState.current.timeout); // Cancel the timeout as we know it isn't wanted now
          refTouchState.current.timeout = undefined;
          refTouchState.current.isLongTouch = false;
        } else {
          // This touch has already been handled by handleTouchTimeout so there is no need to do anything with the touch
          // console.log('Long touch, already handled, ignore end');
        }
      }
      if (!isDrag) {
        handleSegmentClickCallback(event, isCtrlEvent(event.originalEvent));
      }
      refTouchState.current = undefined;
    },
  );

  const handleContextMenu = useCallbackWithErrorHandling((e) => {
    // Supress context menu in the case of a long touch
    if (refTouchState.current && refTouchState.current.isLongTouch) {
      e.preventDefault();
    }
  });

  function handleKeyStroke(event) {
    // On Ctrl-z, undo the last route selection action
    handleWindowKeyDownRouteSelectionUndo(
      event,
      dispatch,
      enableRouteSelection,
    );
  }

  const handleMapMoveCallback = useCallbackWithErrorHandling(() => {
    const center = {
      lng: refMap.current.getCenter().lng.toFixed(4),
      lat: refMap.current.getCenter().lat.toFixed(4),
    };
    const newZoom = refMap.current.getZoom().toFixed(2);
    dispatch(setMapZoom(newZoom));
    dispatch(setMapCenter(center));
  });

  function handleMapLoadCallback() {
    setMapReady(true);
    refStyleReady.current = true;
  }

  const handleChartShowHideClickCallback = () => {
    const val = refMaximize.current ? null : MAXIMIZE_MAP;
    dispatch(setMaximize(val));
    MapControl.setValue(Boolean(!val), kChartShowHideId);
  };

  function dispatchSelectionRedraw(force = false) {
    if (refMap.current && refStyleReady.current) {
      // console.log('selection redraw');
      if (!carLayerVisible) {
        mapPopSelectedSegment(refMap.current);
      } else {
        redrawSelectedSegment(
          refMap.current,
          mapStyle,
          refSelection.current.segment,
          refSelection.current.routeSelection,
          refSelection.current.routeFeature,
          refSelection.current.basicRouteInfo,
          force,
          false,
        );
      }
    }
  }

  const handleSourceDataEvent = useCallbackWithErrorHandling(
    (e: mapboxgl.MapSourceDataEvent) => {
      // Given we now rely on the segments being loaded via tiles to render
      // Selected and routes, we have to listen to new tiles being rendered
      // In case the selected segments or any of the route segments are suddenly in view
      if (e.sourceId === kSegmentSourceId) {
        dispatchSelectionRedraw();
      }
    },
  );

  const handleMapSettingsClickCallback = () => {
    setSettingsOpen(true);
  };

  useEffect(() => {
    const unmount = () => {
      // console.log('Unmount map');
      setMapReady(false);
      refStyleReady.current = false;
      // Wipe old map
      if (refMap.current) {
        refMap.current.remove();
        refMap.current = undefined;
      }
    };
    // Wipe old map
    if (refMap.current) {
      refMap.current.remove();
      refMap.current = undefined;
      setMapReady(false);
      refStyleReady.current = false;
    }

    const { center, zoom } = getInitialCenter(
      userProject,
      currentMapCenter,
      mapZoom,
    );
    if (!center) {
      // Project defaults not yet loaded, skipping initialisation of map
      return unmount;
    }
    // Init new map
    initMap(
      refMap,
      refMapContainer,
      center,
      zoom,
      mapStyle,
      true,
      {
        handleMapLoadCallback,
        handleMapMoveCallback,
        handleSegmentClickCallback: handleClick,
        handleMapSettingsClickCallback,
        handleRoadClassFilterClickCallback,
      },
      dispatch,
      userProject,
      false,
      hideControls,
    );
    refMap.current.on('touchstart', handleTouchStart);
    refMap.current.on('touchend', handleTouchMove);
    refMap.current.on('touchmove', handleTouchMove);
    refMap.current.on('contextmenu', handleContextMenu);
    if (chartShowhide)
      refMap.current.addControl(
        new MapControl(
          kChartShowHideId,
          'Hide/show chart',
          Boolean(!maximize),
          handleChartShowHideClickCallback,
        ),
      );
    refMap.current.on('sourcedata', handleSourceDataEvent);
    return unmount;
  }, [userProject, mapStyle]); // eslint-disable-line react-hooks/exhaustive-deps

  // redrawSegments
  useEffect(
    () => {
      if (!mapReady || !refStyleReady.current || !drawSegmentLayer) return;
      // console.log(`============= useeffect redrawSegments (mapReady: ${mapReady} layerData ${refSegments.current.length}`);
      redrawBaseSegments(
        mapReady,
        refMap.current,
        [getSegmentLayerDefinition(mapStyle, roadFilters)],
        layer ? `server/tiles/${layer}` : undefined,
      );
      // Redraw selected segments on layer change!
      dispatchSelectionRedraw(true);
    },
    // note - we specify layerData as dependency because change to mutable refSegments doesn't rerender
    // we don't need dependency on selectedSegmentId even though we draw the selected statement
    // because (besides initial render) it only erasing/drawing it is handled by handleSegmentClickCallback
    [mapReady, layerData, drawSegmentLayer, mapStyle], // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    refSelection.current.segment = selectedSegmentId;
    refSelection.current.routeSelection = selectedRoute;
    if (showRoutes) dispatchSelectionRedraw();
  }, [selectedSegmentId, selectedRoute]);

  useEffect(() => {
    refSelection.current.routeFeature = routeFeature;
    refSelection.current.savedRoute = savedRoute;
    refSelection.current.basicRouteInfo = basicRouteInfo;
  }, [routeFeature, savedRoute, basicRouteInfo]);

  useEffect(() => {
    if (mapReady) {
      dispatchSelectionRedraw();
    }
  }, [routeFeature, savedRoute, basicRouteInfo, mapReady]);

  useEffect(() => {
    refMaximize.current = maximize;
    if (refMap.current) refMap.current.resize();
  }, [mapReady, maximize, hideSidebar]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (refMap.current) {
      const { center } = getInitialCenter(
        userProject,
        currentMapCenter,
        mapZoom,
      );
      refMap.current.jumpTo({ center });
    }
  }, [userProject]);

  useEffect(() => {
    updateMapLocation(refMap.current, mapZoom, currentMapCenter);
  }, [currentMapCenter, mapZoom]);

  useEffect(() => {
    refInLocationEditMode.current = inLocationEditMode;
    if (mapReady) {
      if (inLocationEditMode) {
        if (!displayedBookmark) {
          dispatch(setDisplayedBookmark({ ...refMap.current.getCenter() }));
        } else {
          const bookmarks = {
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                geometry: latLngToJsonPoint(displayedBookmark),
              },
            ],
          };
          showBookmarks(refMap.current, bookmarks, mapStyle);
        }
      } else {
        safeRemoveBookmarks(refMap.current);
      }
    }
  }, [mapReady, dispatch, inLocationEditMode, displayedBookmark]);

  useEffect(() => {
    // Add Key listener to the entire document to capture ctrl z events
    document.addEventListener('keydown', handleKeyStroke);
    // Don't forget to clean up
    return function cleanup() {
      document.removeEventListener('keydown', handleKeyStroke);
    };
  }, [handleKeyStroke]);

  const screenshotInfo = timeOfDay && {
    time: formatTimeOfDay(timeOfDay),
  };
  return (
    <div ref={refMapContainer} className={styles.map}>
      <MapLoadingSpinner currentRefMap={refMap} mapReady={mapReady} />
      <OutsideBboxPrompt currentRefMap={refMap} />
      <MapSettings
        open={settingsOpen}
        handleVisSwitch={(visible) => setSettingsOpen(visible)}
      />
      <RoadClassTooltip
        open={roadFilterOpen}
        handleVisSwitch={(visible) => setRoadFilterOpen(visible)}
        anchorEl={anchorEl}
      />
      <ScreenshotExtras info={screenshotInfo} />
      {extras}
    </div>
  );
}
